import React from "react";
import { Search, Download } from "lucide-react";
import { Combobox } from "components/ui/combobox";
import { MultiSelectCombobox } from "components/ui/multi-select-combobox";
import { Skeleton } from "components/ui/skeleton";
import type { RNSLevel } from "../api/index";

interface Props {
    levels: RNSLevel[];
    loading: boolean;
    onSelectChange: (levelId: number | string, value: string[]) => void;
    search: string;
    onSearchChange: (val: string) => void;
    onDownloadExcel: () => void;
    downloadingExcel: boolean;
}

const RNSDropdowns: React.FC<Props> = ({levels, loading, onSelectChange, search, onSearchChange, onDownloadExcel, downloadingExcel}) => {
    const visibleLevelsDesc = (levels || [])
        .filter((l) => Boolean(l.visible))
        .sort((a, b) => Number(b.id) - Number(a.id));

    return (
        <div className="shrink-0 space-y-4">
            <div className="flex flex-wrap gap-4 items-end min-h-[72px]">
                {!levels || levels.length === 0 ? (
                    Array.from({ length: 4 }).map((_, index) => (
                        <div className="min-w-[140px] flex-1" key={index}>
                            <label className="block text-sm font-medium mb-1 text-gray-700 relative">
                                <span className="invisible">Placeholder</span>
                                <Skeleton className="h-4 w-20 absolute left-0 top-1/2 -translate-y-1/2" />
                            </label>
                            <Skeleton className="h-10 w-full" />
                        </div>
                    ))
                ) : (
                    visibleLevelsDesc.map((level) => {
                        const baseOpts = (level.options || []).map((o) => ({
                            value: o.ou_code,
                            label: o.ou_name,
                        }));

                        const lockedCodes = Array.isArray(level.locked) ? level.locked : [];
                        const isLockedLevel = lockedCodes.length > 0;

                        return (
                            <div key={String(level.id)} className="min-w-[140px] flex-1">
                                <label className="block text-sm font-medium mb-1 text-gray-700">
                                    {level.name}
                                </label>
                                {Number(level.id) === 1 ? (
                                    <MultiSelectCombobox
                                        options={baseOpts}
                                        value={isLockedLevel ? lockedCodes : level.selected || []}
                                        disabled={loading || isLockedLevel}
                                        onValueChange={(val) => onSelectChange(level.id, val)}
                                        placeholder="-- select --"
                                        searchPlaceholder={`Search ${level.name.toLowerCase()}...`}
                                        emptyText={`No ${level.name.toLowerCase()} found.`}
                                        buttonClassName="h-10 px-2.5 text-sm"
                                    />
                                ) : (
                                    <Combobox
                                        className="h-10 text-sm"
                                        options={baseOpts}
                                        value={isLockedLevel ? lockedCodes[0] || "" : (level.selected && level.selected[0]) || ""}
                                        disabled={loading || isLockedLevel}
                                        onValueChange={(val) => onSelectChange(level.id, val ? [val] : [])}
                                        placeholder="-- select --"
                                        searchPlaceholder={`Search ${level.name.toLowerCase()}...`}
                                        emptyText={`No ${level.name.toLowerCase()} found.`}
                                    />
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4 pt-2">
                <div className="w-[320px] max-w-full">
                    <label className="block text-sm font-medium mb-1 text-gray-700">Search Customer</label>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => onSearchChange(e.target.value)}
                            placeholder="Search by customer code or name..."
                            className="w-full h-10 rounded-md border border-gray-300 bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                </div>
                <div>
                    <button
                        className="px-4 py-2 text-sm rounded-md border border-green-600 text-green-700 hover:bg-green-50 transition-colors flex items-center gap-1.5 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={onDownloadExcel}
                        disabled={downloadingExcel}
                    >
                        <Download size={16} />
                        {downloadingExcel ? "Downloading..." : "Excel"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RNSDropdowns;
